// src/firebase/oauth-auth.ts
import { 
  OAuthProvider, 
  signInWithRedirect, 
  getRedirectResult,
  signInWithPopup 
} from "firebase/auth";
import { auth } from "./firebase";

// Auth0 OAuth provider configuration
// Provider ID is defined in Firebase Console: Auth → Sign-in method → OpenID Connect
// Ensure this matches your configured provider (e.g., "oidc.visit-bergamo").
// You can override via env VITE_OIDC_PROVIDER_ID or URL ?providerId=...
const DEFAULT_OIDC_PROVIDER_ID = "oidc.visit-bergamo"; // aligns with current console config
const FALLBACK_OIDC_PROVIDER_ID = "oidc.travelinauth"; // legacy id kept as fallback
const AUTH0_DOMAIN = "login.visitbergamo.net";
// Note: For Firebase OIDC providers, client id/secret are configured server-side in Firebase Console.
// We keep the constant only for docs/testing components; it is NOT sent from the SPA.
const AUTH0_CLIENT_ID = "9FGFJ6Kp3d6ZyDxKCcLX1TjaurbpuqBP";
const OAUTH_HANDLER_URL = "https://travelinauth.firebaseapp.com/__/auth/handler";

function isPopupBlockedError(error: any): boolean {
  const code = (error as any)?.code || '';
  const msg = (error as any)?.message || '';
  return typeof code === 'string' && code.includes('popup-blocked') || typeof msg === 'string' && msg.toLowerCase().includes('popup blocked');
}

let popupPermissionPromptShown = false;

async function startAuthWithPopupFallback(provider: OAuthProvider, label: string, allowRedirectFallback: boolean = true) {
  try {
    return await signInWithPopup(auth, provider);
  } catch (error) {
    if (isPopupBlockedError(error)) {
      if (!allowRedirectFallback) {
        const alreadyPrompted = popupPermissionPromptShown;
        if (!alreadyPrompted) {
          popupPermissionPromptShown = true;
          try { window.alert('Permettere l\'apertura di popup'); } catch {}
        }
        // Retry once to surface/consume the permission after user action.
        try {
          return await signInWithPopup(auth, provider);
        } catch (retryErr) {
          // If we already prompted and a second attempt also failed with a popup block, propagate.
          if (isPopupBlockedError(retryErr) && alreadyPrompted) {
            throw retryErr;
          }
          // If not blocked, propagate the real error.
          throw retryErr;
        }
      } else {
        void label;
        await signInWithRedirect(auth, provider);
        return null;
      }
    }
    throw error;
  }
}

/**
 * Normalize prompt values so common typos (e.g., prompt=nome) still map to silent auth.
 */
export function normalizePromptParam(raw?: string | null): string | null {
  if (raw == null) return null;
  const v = raw.trim().toLowerCase();
  if (!v) return null;
  if (v === 'nome') return 'none';
  return v;
}

function stripAuthUrlParams() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    ['prompt','authDebug','providerId','login_hint','organization','code','state','error','error_description']
      .forEach((k) => { if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; } });
    if (changed) window.history.replaceState(null, '', url.toString());
  } catch {/* ignore */}
}

function markSilentFailed() {
  try { sessionStorage.setItem('auth0:silent-failed', '1'); } catch {}
}

function hasSilentFailed(): boolean {
  try { return sessionStorage.getItem('auth0:silent-failed') === '1'; } catch { return false; }
}

/**
 * Resolve OIDC provider id from URL → env → defaults.
 * Examples:
 *   ?providerId=oidc.visit-bergamo
 *   ?authDebug=provider:oidc.travelinauth
 */
export function resolveProviderId(): string {
  try {
    const url = new URL(window.location.href);
    const direct = url.searchParams.get('providerId');
    if (direct) return direct;
    const dbg = url.searchParams.get('authDebug');
    if (dbg && dbg.startsWith('provider:')) return dbg.slice('provider:'.length);
  } catch {}
  const fromEnv = (import.meta as any)?.env?.VITE_OIDC_PROVIDER_ID;
  return fromEnv || DEFAULT_OIDC_PROVIDER_ID;
}

/**
 * Creates and configures the Auth0 OAuth provider.
 * If minimal=true, only adds core OIDC scopes (openid profile email) and no extra parameters.
 */
export function createTravelinOAuthProvider(customParams?: Record<string, string>, minimal: boolean = false, explicitProviderId?: string): OAuthProvider {
  const providerId = explicitProviderId || resolveProviderId();
  const provider = new OAuthProvider(providerId);

  // Remove prompt=none as requested by Auth0 to avoid forced silent attempts
  const sanitizedCustomParams: Record<string, string> = { ...(customParams || {}) };
  if (normalizePromptParam(sanitizedCustomParams.prompt ?? null) === 'none') {
    delete sanitizedCustomParams.prompt;
  }
  
  // Set custom parameters for Auth0
  if (!minimal) {
    provider.setCustomParameters({
      // Auth0 specific parameters
      'response_type': 'code',
      'access_type': 'offline',
      'audience': `https://${AUTH0_DOMAIN}/api/v2/`,
      // Custom parameters for manager structures and roles
      'manager_structures': 'true',
      'roles': 'structure_manager,portal_manager',
      // Allow callers to override or add params (prompt=none is stripped upstream)
      ...sanitizedCustomParams,
    });
  } else if (customParams && Object.keys(customParams).length) {
    // In minimal mode, still allow essential overrides (prompt=none removed)
    provider.setCustomParameters({ ...sanitizedCustomParams });
  }

  // --- Dynamic URL-driven optional parameters to help silent auth scenarios ---
  // Supports:
  //   ?login_hint=<email>
  //   ?organization=<org_id or slug>
  //   ?authDebug=hint:<email>
  //   ?authDebug=org:<org_id>
  try {
    const url = new URL(window.location.href);
    const loginHint = url.searchParams.get('login_hint');
    const organization = url.searchParams.get('organization');
    const authDebug = url.searchParams.get('authDebug');
    let hintFromDebug: string | null = null;
    let orgFromDebug: string | null = null;
    if (authDebug) {
      if (authDebug.startsWith('hint:')) hintFromDebug = authDebug.slice('hint:'.length);
      if (authDebug.startsWith('org:')) orgFromDebug = authDebug.slice('org:'.length);
    }
    const effectiveLoginHint = loginHint || hintFromDebug;
    const effectiveOrganization = organization || orgFromDebug;
    const dynamicParams: Record<string,string> = {};
    if (effectiveLoginHint) dynamicParams['login_hint'] = effectiveLoginHint;
    if (effectiveOrganization) dynamicParams['organization'] = effectiveOrganization; // Auth0 orgs (if enabled)
    if (Object.keys(dynamicParams).length) {
      // Merge (do not overwrite existing explicit custom params passed earlier)
      provider.setCustomParameters({ ...(dynamicParams) });
    }
  } catch {/* ignore URL parsing issues */}

  // Add required scopes for Auth0
  provider.addScope('openid');
  provider.addScope('profile'); 
  provider.addScope('email');
  if (!minimal) {
    // Auth0 custom scopes for your application
    provider.addScope('read:structures');
    provider.addScope('manage:structures');
    provider.addScope('read:roles');
  }

  return provider;
}

/**
 * Initiates OAuth sign-in with redirect flow
 */
export async function signInWithTravelinOAuth(): Promise<void> {
  try {
    const provider = createTravelinOAuthProvider();
    await startAuthWithPopupFallback(provider, 'default', false);
  } catch (error) {
    throw error;
  }
}

/**
 * Initiates OAuth sign-in with an explicit prompt value (e.g. 'none' for silent, 'login' for interactive)
 */
export async function signInWithTravelinOAuthWithPrompt(prompt?: string, extraParams?: Record<string, string>): Promise<void> {
  const normalizedPrompt = normalizePromptParam(prompt ?? null) || undefined;
  const promptLabel = normalizedPrompt ?? 'auto';
  try {
    // Use minimal params for silent (prompt=none) and interactive (prompt=login) to avoid Firebase createAuthUri 400s
    // on custom parameters like audience/manager_structures/roles
    const minimal = normalizedPrompt === 'none' || normalizedPrompt === 'login' ? true : shouldUseMinimalAuthParams();
    const shouldSendPromptParam = normalizedPrompt !== undefined && normalizedPrompt !== 'none';
    const params: Record<string, string> = { ...(extraParams || {}) };
    if (shouldSendPromptParam) params.prompt = normalizedPrompt;

    const provider = createTravelinOAuthProvider(params, minimal);
    await startAuthWithPopupFallback(provider, `prompt=${promptLabel}${minimal ? ', minimal' : ''}`, false);
  } catch (error) {
    // If we failed with the richer parameter set, try again with minimal params to bypass createAuthUri 400s
    try {
      const minimal = true;
      const params: Record<string, string> = {};
      if (normalizedPrompt && normalizedPrompt !== 'none') params.prompt = normalizedPrompt;
      // Preserve caller-supplied hints only, skip custom scopes/extra audience
      if (extraParams) {
        const { login_hint, organization } = extraParams as any;
        if (login_hint) params.login_hint = login_hint;
        if (organization) params.organization = organization;
      }
      const provider = createTravelinOAuthProvider(params, minimal);
      await startAuthWithPopupFallback(provider, `prompt=${promptLabel}, minimal fallback`, false);
      return;
    } catch (minimalErr) {
      void minimalErr;
    }
    // Optional fallback: if primary provider id is not usable, try legacy fallback once
    try {
      const current = resolveProviderId();
      if (current === DEFAULT_OIDC_PROVIDER_ID && FALLBACK_OIDC_PROVIDER_ID) {
        void FALLBACK_OIDC_PROVIDER_ID;
        const minimal = shouldUseMinimalAuthParams();
        const params: Record<string, string> = { ...(extraParams || {}) };
        if (normalizedPrompt) params.prompt = normalizedPrompt;
        const provider = createTravelinOAuthProvider(params, minimal, FALLBACK_OIDC_PROVIDER_ID);
        await startAuthWithPopupFallback(provider, `prompt=${promptLabel}, fallback provider`, false);
        return;
      }
    } catch (fallbackErr) {
      void fallbackErr;
    }
    throw error;
  }
}

/**
 * Initiates OAuth sign-in with popup flow (alternative to redirect)
 */
export async function signInWithTravelinOAuthPopup() {
  try {
    const minimal = shouldUseMinimalAuthParams();
    const provider = createTravelinOAuthProvider(undefined, minimal);
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error) {
    throw error;
  }
}

/**
 * Handles the redirect result after OAuth authentication
 * Call this on app initialization to handle returning users
 */
export async function handleOAuthRedirectResult(): Promise<
  | { user: any; credential: any; customClaims: any }
  | null
> {
  try {
    // Capture whether URL carried redirect params for enhanced diagnostics
    let hadUrlParams = false;
    try {
      const u = new URL(window.location.href);
      hadUrlParams = ['code','error','state'].some(k => u.searchParams.has(k));
      void hadUrlParams;
      void u;
    } catch {}
    const result = await getRedirectResult(auth);
    
    if (result) {
      const user = result.user;
      const credential = OAuthProvider.credentialFromResult(result);

      // Extract custom claims from the token if available
      const idTokenResult = await user.getIdTokenResult();
      const customClaims = idTokenResult.claims;
      void customClaims;

      return {
        user,
        credential,
        customClaims
      };
    }

    if (hadUrlParams) {
      // Log redirect-related storage artifacts for deeper diagnosis
      try {
        const lsKeys = Object.keys(localStorage).filter(k => k.includes('firebase') && k.toLowerCase().includes('redirect'));
        const ssKeys = Object.keys(sessionStorage).filter(k => k.includes('firebase') && k.toLowerCase().includes('redirect'));
        void lsKeys;
        void ssKeys;
      } catch {}
      // Clean URL params to break potential redirect loops that keep prompt=none or code lingering
      stripAuthUrlParams();
    }
    return null;
  } catch (error) {
    // Surface parseable info but do not throw to allow callers to decide fallbacks.
    // Auth0 silent auth (prompt=none) commonly returns error "login_required".
    try {
      const code = (error as any)?.code || (error as any)?.error || 'unknown';
      const desc = (error as any)?.message || (error as any)?.error_description;
      void code;
      void desc;
      if (code === 'login_required' || code === 'interaction_required') {
        // Avoid repeat silent redirects when Auth0 says user must log in
        markSilentFailed();
        stripAuthUrlParams();
      }
    } catch {}
    return null;
  }
}

/**
 * Get managed structures from user claims (Auth0 format + Visit custom claims)
 */
export async function getManagedStructuresFromClaims(): Promise<string[]> {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    // Auth0 uses namespaced claims; also support Visit custom claims
    const managedStructures = claims['https://travelin.com/manager_structures'] || 
                              claims['manager_structures'] ||
                              claims['app_metadata']?.manager_structures ||
                              claims['managed_structures'];
    
    return Array.isArray(managedStructures) ? managedStructures : [];
  } catch (error) {
    void error;
    return [];
  }
}

/**
 * Get user roles from claims (Auth0 format + Visit custom claims)
 */
export async function getUserRolesFromClaims(): Promise<string[]> {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    // Auth0 uses namespaced claims and roles array; also support Visit custom claims
    const roles = claims['https://travelin.com/roles'] || 
                  claims['roles'] ||
                  claims['app_metadata']?.roles ||
                  claims['authorization']?.roles ||
                  claims['visit_role'];
    
    return Array.isArray(roles) ? roles : [];
  } catch (error) {
    void error;
    return [];
  }
}

/**
 * Check if the current user has structure manager access (Auth0 format + Visit custom claims)
 */
export async function hasStructureManagerAccess(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    // Check for managed structures
    const managedStructures = claims['https://travelin.com/manager_structures'] || 
                              claims['manager_structures'] ||
                              claims['app_metadata']?.manager_structures ||
                              claims['managed_structures'];
    
    if (Array.isArray(managedStructures) && managedStructures.length > 0) {
      return true;
    }

    // Check for structure manager role
    const roles = claims['https://travelin.com/roles'] || 
                  claims['roles'] ||
                  claims['app_metadata']?.roles ||
                  claims['authorization']?.roles ||
                  claims['visit_role'];
                  
    if (Array.isArray(roles) && roles.includes('structure_manager')) {
      return true;
    }

    // Check Auth0 roles array (alternative format)
    if (Array.isArray(roles)) {
      const hasManagerRole = roles.some(role => 
        typeof role === 'string' && role.includes('structure_manager') ||
        typeof role === 'object' && role.name === 'structure_manager'
      );
      if (hasManagerRole) return true;
    }

    return false;
  } catch (error) {
    void error;
    return false;
  }
}

/**
 * Silent auth helper: if URL has ?prompt=none and no Firebase user session, trigger an
 * Auth0 silent authorization attempt via redirect. Uses a sessionStorage guard to avoid loops.
 *
 * Scenario mapping:
 * - Scenario 1: Already authenticated on VisitBergamo → silent auth succeeds, Firebase signs in.
 * - Scenario 2: Not authenticated on Auth0 → Auth0 returns login_required; we will not loop here,
 *   and the app can then initiate an interactive login (prompt='login').
 */
export async function attemptSilentAuthIfNeeded(): Promise<{ started: boolean }> {
  try {
    const url = new URL(window.location.href);
    const prompt = normalizePromptParam(url.searchParams.get('prompt'));
    if (prompt !== 'none') return { started: false };

    // If silent auth already failed in this tab, don't retry to avoid loops
    if (hasSilentFailed()) return { started: false };

    // If a Firebase user already exists, nothing to do
    if (auth.currentUser) return { started: false };

    // Guard against infinite redirects per-tab
    const GUARD_KEY = 'auth0:silent-attempted';
    if (sessionStorage.getItem(GUARD_KEY) === '1') {
      return { started: false };
    }
    sessionStorage.setItem(GUARD_KEY, '1');

    // Optional: forward login_hint if provided by referrer
    const loginHint = url.searchParams.get('login_hint') || undefined;
    const extra: Record<string, string> = {};
    if (loginHint) extra['login_hint'] = loginHint;

    await signInWithTravelinOAuthWithPrompt('none', extra);
    // We won't get here due to redirect
    return { started: true };
  } catch (e) {
    void e;
    return { started: false };
  }
}

// --- Debug/diagnostics helpers ---

/**
 * If URL has authDebug=minimal, use minimal OIDC parameters (no audience/custom scopes).
 */
export function shouldUseMinimalAuthParams(): boolean {
  try {
    const url = new URL(window.location.href);
    const dbg = url.searchParams.get('authDebug');
    const prompt = normalizePromptParam(url.searchParams.get('prompt'));
    // Force minimal when prompt=none to keep silent auth strictly OIDC-basic
    if (prompt === 'none') return true;
    return dbg === 'minimal';
  } catch {
    return false;
  }
}

/**
 * Diagnostics guide (for operators):
 * - 400 on createAuthUri usually means provider misconfiguration in Firebase Console.
 *   Check: Auth → Sign-in method → OpenID Connect
 *   - Provider ID matches resolveProviderId() (e.g., oidc.visit-bergamo)
 *   - Issuer has https:// scheme: https://login.visitbergamo.net/
 *   - Application type in Auth0: Regular Web Application (has client secret)
 *   - Correct client ID and secret are saved
 */

// === Visit Custom Claims Helpers ===

/**
 * Get Visit portals from claims (Visit custom claim)
 */
export async function getVisitPortalsFromClaims(): Promise<string[]> {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    const portals = claims['visit_portals'];
    return Array.isArray(portals) ? portals : [];
  } catch (error) {
    void error;
    return [];
  }
}

/**
 * Get user source from claims (Visit custom claim)
 */
export async function getUserSourceFromClaims(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    const userSource = claims['user_source'];
    return typeof userSource === 'string' ? userSource : null;
  } catch (error) {
    void error;
    return null;
  }
}

/**
 * Check if user is disabled from claims (Visit custom claim)
 */
export async function isUserDisabledFromClaims(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    
    return Boolean(claims['disabled']);
  } catch (error) {
    void error;
    return false;
  }
}

/**
 * Diagnostics guide (for operators):
 * - 400 on createAuthUri usually means provider misconfiguration in Firebase Console.
 *   Check: Auth → Sign-in method → OpenID Connect
 *   - Provider ID matches resolveProviderId() (e.g., oidc.visit-bergamo)
 *   - Issuer has https:// scheme: https://login.visitbergamo.net/
 *   - Application type in Auth0: Regular Web Application (has client secret)
 *   - Correct client ID and secret are saved
 *   - Callback URL present: https://travelinauth.firebaseapp.com/__/auth/handler
 *   - For testing, add ?authDebug=minimal to reduce params, or ?providerId=...
 */
